import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import { DragEndEvent } from "@dnd-kit/core";
import { useIntl } from "react-intl";

import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { translateMessage } from "src/app/methods/translateMessage";
import { VerticalDragAndDropList } from "src/app/components/VerticalDragAndDropList/VerticalDragAndDropList";
import { RootState } from "src/redux/reducers";
import {
  GlobalProjectMetaField,
  GlobalTaskMetaField,
} from "src/redux/settings/settingsReducer";
import { TaskType } from "src/types";
import {
  FieldOption,
  HandleAddFieldsProps,
  HandleRemoveFieldsProps,
  HandleUpdateTemplateValueParams,
  Template,
} from "../types";
import {
  handleUpdateProjectTemplate,
  mapFieldTypeToFieldName,
  rearrangeElementsInArray,
} from "../utils";
import TaskTypeTabs from "./TaskTypeTabs";
import DraggableFieldListItem from "./DraggableFieldListItem";

interface FieldsTabProps {
  editedTemplate: Template;
  handleUpdateTemplateValue: (params: HandleUpdateTemplateValueParams) => void;
  handleAddFields: (params: HandleAddFieldsProps) => void;
  handleRemoveFields: (params: HandleRemoveFieldsProps) => void;
  fieldType: "project" | "task";
  fields: Array<GlobalProjectMetaField | GlobalTaskMetaField>;
  setEditedTemplate: Dispatch<SetStateAction<Template | null>>;
  syncLocalTemplateWithServer: (uuid: string) => Promise<void>;
}

export default function FieldsTab({
  editedTemplate,
  handleUpdateTemplateValue,
  handleAddFields,
  handleRemoveFields,
  fieldType,
  fields,
  setEditedTemplate,
  syncLocalTemplateWithServer,
}: FieldsTabProps) {
  const [selectedTaskType, setSelectedTaskType] = useState<TaskType>(
    TaskType.Creator,
  );

  const [selectedFields, setSelectedFields] = useState<Array<FieldOption>>([]);

  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    mainReducer: { activeWorkspaceUuid },
  } = useSelector((state: RootState) => state);

  const fieldsFromTemplate = useMemo(
    () =>
      editedTemplate[mapFieldTypeToFieldName[fieldType]][selectedTaskType] ??
      [],
    [editedTemplate, fieldType, selectedTaskType],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = selectedFields.findIndex(
      (field) => field.value === active.id,
    );
    const newIndex = selectedFields.findIndex(
      (field) => field.value === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Update the visual order of selectedFields
    const rearrangedSelectedFields = rearrangeElementsInArray(
      selectedFields,
      oldIndex,
      newIndex,
    );
    setSelectedFields(rearrangedSelectedFields);

    setEditedTemplate((prevTemplate) => {
      if (!prevTemplate) return null;

      const updatedTemplate = { ...prevTemplate };

      const fields =
        updatedTemplate[mapFieldTypeToFieldName[fieldType]][selectedTaskType];

      if (fields) {
        const rearrangedFields = rearrangeElementsInArray(
          fields,
          oldIndex,
          newIndex,
        );

        updatedTemplate[mapFieldTypeToFieldName[fieldType]][selectedTaskType] =
          rearrangedFields;
      }

      handleUpdateProjectTemplate({
        data: {
          wsWorkspaceUuid: activeWorkspaceUuid,
          ...updatedTemplate,
        },
        catchCallback: () => syncLocalTemplateWithServer(editedTemplate.uuid),
        dispatch,
      });

      return updatedTemplate;
    });
  };

  const handleChange = (newFields: FieldOption[]) => {
    setSelectedFields(newFields);

    const newAttributes = newFields.filter(
      (attr) =>
        fieldsFromTemplate.findIndex((field) => field.uuid === attr.value) ===
        -1,
    );

    if (newAttributes.length === 0) {
      const removedFieldsIds = fieldsFromTemplate
        .filter(
          (field) =>
            newFields.findIndex((attr) => attr.value === field.uuid) === -1,
        )
        .map((field) => field.uuid);

      handleRemoveFields({
        fieldsToRemove: removedFieldsIds,
        fieldType,
        taskType: selectedTaskType,
      });
    } else {
      const mapNewAttributesToFieldData = newAttributes.map((attr) => ({
        uuid: attr.value,
        hide: false,
      }));

      handleAddFields({
        newFieldsData: mapNewAttributesToFieldData,
        fieldType,
        taskType: selectedTaskType,
      });
    }
  };

  const fieldOptions = useMemo(
    () =>
      fields
        .filter(
          (field) =>
            field.taskType === selectedTaskType || field.taskType === null,
        )
        .map((filteredField) => {
          return {
            label: filteredField.name,
            value: filteredField.uuid,
          };
        }),
    [fields, selectedTaskType],
  );

  useEffect(() => {
    const alreadySelectedFields: FieldOption[] = [];

    fieldsFromTemplate.forEach((fieldFromTemplate) => {
      const foundFieldOption = fieldOptions.find(
        (fieldOption) => fieldOption.value === fieldFromTemplate.uuid,
      );

      if (foundFieldOption) {
        alreadySelectedFields.push(foundFieldOption);
      }
    });

    setSelectedFields(alreadySelectedFields);
  }, [fieldOptions, fieldsFromTemplate]);

  return (
    <>
      <TaskTypeTabs
        selectedTaskType={selectedTaskType}
        setSelectedTaskType={setSelectedTaskType}
      />
      <div className="template-manager__headers grid--fields">
        <div>
          <IDHFormattedMessage
            id="ws_table_column"
            defaultMessage="Table Column"
          />
        </div>
        <div>
          <IDHFormattedMessage id="ws_hide" defaultMessage="Hide" />
        </div>
        <div />
      </div>
      <div className="template-manager__selector">
        <CustomSelect
          options={fieldOptions}
          value={selectedFields}
          closeMenuOnSelect={false}
          isClearable={false}
          isMulti
          placeholder={translateMessage({
            intl,
            id:
              fieldType === "project"
                ? "ws_select_overview"
                : "ws_select_table_column",
            defaultMessage:
              fieldType === "project"
                ? "Select overview"
                : "Select table column",
          })}
          onChange={handleChange}
        />
      </div>
      <VerticalDragAndDropList.DndContext onDragEnd={handleDragEnd}>
        <VerticalDragAndDropList.SortableContext
          items={selectedFields.map((field) => field.value)}
        >
          <ul className="template-manager__list template-manager__list--fields">
            {selectedFields.map((option: FieldOption) => {
              const foundGlobalField = fields.find(
                (field) => field.uuid === option.value,
              );
              const foundFieldFromTemplate = fieldsFromTemplate.find(
                (field) => field.uuid === option.value,
              );

              if (!foundGlobalField || !foundFieldFromTemplate) return null;

              return (
                <DraggableFieldListItem
                  key={option.value}
                  option={option}
                  globalField={foundGlobalField}
                  templateField={foundFieldFromTemplate}
                  handleUpdateTemplateValue={handleUpdateTemplateValue}
                  handleRemoveFields={handleRemoveFields}
                  fieldType={fieldType}
                  taskType={selectedTaskType}
                />
              );
            })}
          </ul>
        </VerticalDragAndDropList.SortableContext>
      </VerticalDragAndDropList.DndContext>
    </>
  );
}
